/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import cx from 'classnames';
import Head from 'next/head';
import { type Ref, Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import root from 'window-or-global';
import useLatestCallback from 'use-latest-callback';
import usePrevious from '@/utils/usePrevious';
import type { HandleFilterType } from '@/types/filterTypes';
import {
  removeDataPart,
  setProductFilterEvents,
  setFilterScroll,
  setFilterScrollBrand,
} from '@/actions/shelf/shelf';
import { routePaths } from '@/constants/routePaths';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import azCommonStyles from '@/theme/globals.module.scss';
import type { ReduxState } from '@/types';
import { type AddCertonaItemObjectType } from '@/features/certona';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { MiniCartModal } from '@/components/Cart/MiniCartModal';
import ShelfSkeleton from '@/components/Skeletons/ShelfSkeleton';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import { useScrollToRef } from '@/hooks/useScrollToRef';
import styles from './styles.module.scss';
import { PreFooter } from '@/features/prefooter';
import { useAppState } from '@/stores/AppState';
import { BazaarVoiceShelf } from '../BazaarVoiceShelf/BazaarVoiceShelf';
import { useHeaderData } from '@/features/header/api/getHeader';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useProductList } from '../../context/useProductList';
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useQueryClient } from '@tanstack/react-query';
import { useDeviceType } from '@/utils/useDeviceType';
import { useFeatureFlag } from '@/features/globalConfig';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { sessionStorage } from '@/utils/sessionStorage';
import { MINI_CART_TITLE_ID, MiniCartFlyoutComp } from '@/features/miniCartFlyout';
import { Drawer } from '@/components/Drawer/Drawer';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import Hidden from '@/components/Hidden';
import { useBrandProductData } from '@/features/brands/api/getProductBrandData';
import { useDealDrawer } from '@/features/deals/hooks/useDealDrawer';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import { useProductListView } from '../../context/useProductListView';
import { deleteFromHistoryStateOptions } from '@/features/common/utils/deleteFromHistoryStateOptions';
import { useSeoMetadata } from '@/features/common/api/getSeoMetadata';
import { FilterMobileDrawer } from '@/components/Filter/FilterMobileDrawer';
import dynamic from 'next/dynamic';
import { useSearchProductData } from '@/features/search/api/getSearchProductData';
import { useAutoCloseOnOrderReady } from '@/features/sisterStoreFlyout';
import { CMSShelfResultHeaderModularHandler, CMSShelfLeftNav } from './CMSModularHandler';
import { CMSResultBottom } from './CMSModularHandler/CMSResultBottom';
import { usePageType as usePageTypeLegacy } from '@/hooks/usePageType';
import { pageTypes as pageTypeConstants } from '@/constants/page';
import { historyStateOptionAttributes } from '@/constants/historyStateOption';
import { formatPartTypeId } from '@/utils/formatPartTypeId';
import { View } from '@az/starc-ui';
import { CMSSeoTags } from '@/features/contentstack/components/CMSSeoTags/CMSSeoTags';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import { CMSPLPBodyModularHandler } from '@/features/productListingPage/components/CMSModularHandler';
import { usePageType } from '@/features/category/api/getPageType';
import { getShelfPageType } from '@/utils/getShelfpageType';
import { useCombinedInfiniteQuery } from '@/hooks/useCombinedInfiniteQuery';
import { useContentStackLivePreview } from '@/hooks/useContentStackLivePreview';
import { ShelfSeoMetaData } from '../ShelfSeoMetaData/ShelfSeoMetaData';
import { useShelfAnalytics } from '../../lib/hooks/useShelfAnalytics';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { type ProductShelfResultsModel } from '@/api/types/browse-search-types';
import { useACESBrandProductData } from '@/features/brands/api/getAcesProductBrandData';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { useMerchShelfResults } from '@/features/category/api/getMerchShelfResults';
import { useAppData } from '@/hooks/useAppData';
import React from 'react';
import { useSeoFaqs } from '@/features/common/api/getSeoFaqs';

const LazyMiniCartFlyoutComp = dynamic(
  () => import('@/features/miniCartFlyout').then((mod) => mod.MiniCartFlyoutComp),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

type ProductShelfProps = {
  shelfRef?: Ref<HTMLDivElement>;
  mainShelfRef?: Ref<HTMLDivElement>;
};

export const ProductShelf = ({ shelfRef, mainShelfRef }: ProductShelfProps) => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  // TO DO add minicartSuccessNote to global data
  const cartSuccessData = useSelector(({ shelf }: ReduxState) => shelf.cartSuccess);
  const readyStatus = useSelector(({ shelf }: ReduxState) => shelf.readyStatus);
  const isRefresh = useSelector(({ shelf }: ReduxState) => shelf.isRefresh);

  const queryClient = useQueryClient();
  const {
    data: plpCMSData,
    isCSProductListingPageEnabled,
    isFetching: isPLPCMSDataFetching,
    isSuccess: isPLPCMSDataSuccess,
    isError: isPLPCMSDataError,
  } = useContentStackPLPData({ enabled: true });
  useContentStackLivePreview({ enabled: isCSProductListingPageEnabled });

  const isShelfCertonaAddToCartEnabled = useCertonaAddToCartFlag();
  const refQuickFilter = useRef<HTMLElement>(null);
  const ymmeRef = useRef<HTMLDivElement>(null);
  const miniCartModalRef = useRef<HTMLDivElement>(null);
  const lazyMiniCartModalRef = useRef<HTMLDivElement>(null);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const previousReadyStatus = usePrevious(readyStatus);
  const { data: headerData } = useHeaderData();
  const { acesEnabled } = useAcesFlags();
  const preferredVehicle = usePreferredVehicle();
  const dispatch = useDispatch();
  const miniCartMap = headerData?.miniCartMap;
  const minicart = Boolean(headerData?.miniCartSuccessNote);
  const {
    state: { campaignModalEnabled },
  } = useAppState();
  const previousCampaignModalEnabled = usePrevious(campaignModalEnabled);
  const { filterScroll } = useSelector(({ shelf }: ReduxState) => shelf);
  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);

  const removeData = () => dispatch(removeDataPart());
  const dispatchSetFilterScroll = (value: boolean) => dispatch(setFilterScroll(value));
  const dispatchSetProductFilterEvents = (
    filterName: string,
    filterId: string,
    checked: boolean,
    quickFilter?: boolean,
  ) => dispatch(setProductFilterEvents(filterName, filterId, checked, quickFilter));

  const isMerchShelfPage = legacyRouter.route === '/deals/[cmsPath]/[[...slug]]';

  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const { data: productShelfResultsData, isFetching: productShelfResultsIsFetching } =
    useShelfResultsFunction();

  const { data: merchShelfResultsData, isFetching: merchShelfResultsIsFetching } =
    useMerchShelfResults();

  const isProductResultsFetching =
    isMerchShelfPage && acesEnabled ? merchShelfResultsIsFetching : productShelfResultsIsFetching;

  // what if we passed this data from useBrand or useCategory? Routing component always uses useCategory and BrandShelf uses useBrand
  const {
    data: shelfData,
    isSuccess: shelfRequestSuccess,
    isFetching: shelfRequestFetching,
    productSkuIsFetching,
    loadMoreClicked,
    additionalProductSkuDetailsIsFetching,
  } = useProductList();

  const isBrandShelf = usePageTypeLegacy().pageType === pageTypeConstants.BrandShelf;
  const { data: searchProductData } = useSearchProductData({ enabled: false });
  const useBrandProductDataFunction = acesEnabled ? useACESBrandProductData : useBrandProductData;

  const { data: brandShelfData, isFetching: isBrandShelfDataFetching } =
    useBrandProductDataFunction({
      enabled: isBrandShelfPage(legacyRouter.asPath),
    });
  const { staticNavigation: brandShelfStaticNavigation = [] } = brandShelfData;

  const { data: pageTypeData } = usePageType();

  const shopByUrlMake = pageTypeData?.make ?? undefined;
  const shopByUrlModel = pageTypeData?.model ?? undefined;
  const shopByUrlYear = pageTypeData?.year ?? undefined;

  const selectedFacetValue =
    productShelfResultsData?.productShelfResults &&
    'selectedFacetValueDisplayName' in productShelfResultsData.productShelfResults
      ? productShelfResultsData.productShelfResults.selectedFacetValueDisplayName
      : undefined;

  const { data: seoMetadataInfo } = useSeoMetadata({
    enabled: !isProductResultsFetching,
    vehicleMake: shopByUrlMake,
    vehicleModel: shopByUrlModel,
    vehicleYear: shopByUrlYear,
    selectedFacetValue,
  });

  const { data: seoFaqsData } = useSeoFaqs();

  useScrollToRef({
    ref: refQuickFilter,
    dependencies: [readyStatus, filterScroll],
    conditions: readyStatus !== previousReadyStatus && shelfRequestSuccess && !!filterScroll,
    afterScroll: () => dispatchSetFilterScroll(false),
  });

  useScrollToRef({
    ref: ymmeRef,
    dependencies: [campaignModalEnabled],
    conditions: !!previousCampaignModalEnabled && !campaignModalEnabled,
    scrollOffset: 60,
  });

  const [showMiniCartModal, setShowMiniCartModal] = useState(false);
  const [certonaRecommendationAddedToCart, setCertonaRecommendationAddedToCart] = useState(false);
  const [addedCertonaItemObject, setAddedCertonaItemObject] =
    useState<AddCertonaItemObjectType | null>(null);
  const [showMiniCartFlyoutModal, setShowMiniCartFlyoutModal] = useState(false);

  const deviceType = useDeviceType();
  const { appData } = useAppData();
  const { isFetchingLocation } = appData;

  const seoTopCopy = seoMetadataInfo?.topContentSeoActive ? seoMetadataInfo.copyBlockOne : null;

  const makeModelYearPageType = shelfData?.makeModelYearPageType
    ? shelfData.makeModelYearPageType
    : shopByUrlMake
      ? shopByUrlModel
        ? shopByUrlYear
          ? 'ShopByYear'
          : 'ShopByModel'
        : 'ShopByMake'
      : null;

  const shelfBreadcrumb = shelfData?.shelfBreadcrumb;
  const shelfLeftNav = shelfData?.shelfLeftNav;
  const shortDescription = seoTopCopy;
  const staticFilterData =
    brandShelfStaticNavigation.length > 0
      ? brandShelfData.staticNavigation
      : shelfLeftNav?.[0]?.staticNavigation;

  const filterNavItem = shelfLeftNav?.[0];
  const hasFilters =
    (filterNavItem?.staticNavigation.length ?? 0) > 0 ||
    (filterNavItem?.dynamicNavigation?.attributeNarrowingList?.length ?? 0) > 0 ||
    brandShelfStaticNavigation.length > 0;

  const parts = shelfData?.shelfParts ?? [];
  const isABot = deviceType === 'bot';
  const shelfProductId = shelfData?.productId;

  const [showCertonaMiniCartFlyoutModal, setShowCertonaMiniCartFlyoutModal] = useState(false);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const desktopMiniCartFlyoutEnabled =
    useFeatureFlag('IS_DESKTOP_MINI_CART_FLYOUT_ENABLED') === 'true';
  const mobileMiniCartFlyoutEnabled =
    useFeatureFlag('IS_MOBILE_MINI_CART_FLYOUT_ENABLED') === 'true';
  const tabletMiniCartFlyoutEnabled =
    useFeatureFlag('IS_TABLET_MINI_CART_FLYOUT_ENABLED') === 'true';
  const isMiniCartFlyoutEnabled =
    (isDesktop && desktopMiniCartFlyoutEnabled) ||
    (isMobile && mobileMiniCartFlyoutEnabled) ||
    (isTablet && tabletMiniCartFlyoutEnabled);

  const [miniCartFlyoutVisible, setMiniCartFlyoutVisible] = useState(false);
  const { isMobileFilterOpen } = useProductListView();
  const closeMiniCartFlyout = () => {
    setMiniCartFlyoutVisible(false);
    removeData();
  };
  const { infiniteQueryIsPending, isFetchingNextPage } = useCombinedInfiniteQuery();
  const showSkeleton =
    !isRefresh &&
    (isFetchingLocation || shelfRequestFetching || !shelfRequestSuccess || infiniteQueryIsPending);

  const productsResponseFailed =
    (!isPLPCMSDataFetching && !isPLPCMSDataSuccess) ||
    (!shelfRequestFetching && !shelfRequestSuccess);
  const shelfCatLabel = showSkeleton
    ? undefined
    : (seoMetadataInfo?.pageHeadingName ??
      plpCMSData?.additional_attributes_merch_shelf_page?.heading);

  const toggleCertonaMiniCartFlyout = () => {
    setShowCertonaMiniCartFlyoutModal(() => !showCertonaMiniCartFlyoutModal);
    closeStoreModal();
  };

  useEffect(() => {
    if (addedCertonaItemObject) {
      setShowCertonaMiniCartFlyoutModal(true);
    }
  }, [addedCertonaItemObject]);

  const isVisualFiltersDisplayedOnShelf = acesEnabled
    ? ((productShelfResultsData?.productShelfResults as ProductShelfResultsModel | undefined)
        ?.visualFilters?.[0]?.facetValues?.length ?? 0) > 1
    : false;

  useShelfAnalytics({
    shelfCatLabel,
    isBrandShelfDataFetching,
    infiniteQueryIsPending,
    isFetchingNextPage,
    brandShelfData,
    searchProductData,
    productShelfResultsData:
      isMerchShelfPage && acesEnabled ? merchShelfResultsData : productShelfResultsData,
    isVisualFiltersDisplayedOnShelf,
  });

  useAutoCloseOnOrderReady();

  useEffect(() => {
    if (
      root.history.state &&
      'options' in root.history.state &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      'latestSelectedPageYOffset' in root.history.state.options
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const latestSelectedPageYOffset = root.history.state.options
        .latestSelectedPageYOffset as string;

      if (
        parseInt(latestSelectedPageYOffset) > 0 &&
        shelfRequestSuccess &&
        !isBrandShelf &&
        !productSkuIsFetching &&
        isPLPCMSDataSuccess
      ) {
        root.scroll({
          top: parseInt(latestSelectedPageYOffset),
        });
        deleteFromHistoryStateOptions(historyStateOptionAttributes.latestSelectedPageYOffset);
      }
    }
  }, [
    isBrandShelf,
    shelfRequestSuccess,
    productSkuIsFetching,
    isPLPCMSDataSuccess,
    loadMoreClicked,
    additionalProductSkuDetailsIsFetching,
    isCSProductListingPageEnabled,
  ]);

  useEffect(() => {
    if (cartSuccessData && minicart) {
      setShowMiniCartModal(true);
      setMiniCartFlyoutVisible(true);
      setShowMiniCartFlyoutModal(true);
    }
  }, [cartSuccessData, minicart]);

  useEffect(() => {
    return () => {
      removeData();
      setMiniCartFlyoutVisible(false);
      setShowMiniCartFlyoutModal(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPLPCMSDataError) {
      void router.replace('/NotFound');
    }
  }, [isPLPCMSDataError, router]);

  const vehiclesInfo = headerData?.vehicleMap;
  const vehicleId = vehiclesInfo?.catalogVehicleId;
  const recordType = productShelfResultsData?.productShelfResults?.skuRecords?.[0]?.recordType;
  const taxonomyId = (
    productShelfResultsData?.productShelfResults as ProductShelfResultsModel | undefined
  )?.taxonomyId;
  const pageType = getShelfPageType(taxonomyId, recordType, acesEnabled);
  const shelfSkuRecords = productShelfResultsData?.productShelfResults?.skuRecords;
  const skuIds = shelfSkuRecords
    ?.slice(0, 10)
    .map((skuRecord) => skuRecord.itemId)
    .join(';');

  const certonaRecommendations = useGetCertonaRecommendations(
    {
      certonaType: pageType!,
      vehicleID: acesEnabled ? preferredVehicle?.partsProVehicleId?.toString() : vehicleId,
      // sending the 1st product's partType as all the shelf product's has the same part type
      parttype: acesEnabled
        ? shelfSkuRecords?.[0]?.partTerminologyId
        : formatPartTypeId(shelfSkuRecords?.[0]?.originalPartTypeId),
      skuIds,
    },
    [shelfRequestSuccess, productShelfResultsData],
    shelfRequestSuccess && Boolean(productShelfResultsData),
    false,
  );

  const closeStoreModal = () => {
    removeData();
    setShowMiniCartModal(false);
    setMiniCartFlyoutVisible(false);
    setShowMiniCartFlyoutModal(false);
  };

  const submitToCart = () => {
    void router.push(routePaths.cart);
  };

  const renderMiniCart = () => {
    const addToCartItem = isABot
      ? parts[0]
      : parts.find((item) => String(item.skuNumber) === cartSuccessData?.skuId);
    const productImage = addToCartItem?.itemImageUrl || addToCartItem?.productImageUrl || '';
    const productName = {
      productBrand: addToCartItem?.brand || '',
      productPart: addToCartItem?.name || '',
      quickNote: addToCartItem?.quickNote || '',
    };
    const productAvailabilityInfo = {
      skuPricingAndAvailability: addToCartItem?.skuPricingAndAvailability,
      dealsInfo: addToCartItem?.dealsInfo,
      aZBadgesFlagVO: addToCartItem?.aZBadgesFlagVO,
      availabilityInfoVO: addToCartItem?.availabilityInfoVO,
    };
    const vehicleSet = !!headerData?.vehicleMap?.vehicleId;
    const vehicleFitFlag =
      addToCartItem != null &&
      (vehicleSet
        ? addToCartItem.vehicleFitment !== 'DOES_NOT_FIT'
        : addToCartItem.vehicleFitment !== 'MAY_NOT_FIT' &&
          addToCartItem.vehicleFitmentLabel !== 'VEHICLE_SPECIFIC');
    const productDetails = {
      productName,
      productAvailabilityInfo,
      productPartType: acesEnabled
        ? addToCartItem?.partGroupId
        : shelfSkuRecords?.[0]?.originalPartTypeId,
      partTerminologyId: addToCartItem?.partTerminologyId ?? addToCartItem?.partGroupId,
      vehicleFitFlag,
    };
    const checkToOpenMiniCartModal =
      miniCartMap &&
      (cartSuccessData || minicart) &&
      (showMiniCartModal || showMiniCartFlyoutModal);

    const renderOldMiniCart = () => {
      return (
        <Hidden only={['sm', 'md']}>
          <MiniCartModal
            // @ts-expect-error fix type
            isModalOpen={global !== 'undefined' ? showMiniCartModal : false}
            image={productImage}
            successData={cartSuccessData}
            productDetails={productDetails}
            closeStoreModal={closeStoreModal}
            cartUrl={submitToCart}
            productTitle={productName.productPart}
            pageName="shelf"
          />
        </Hidden>
      );
    };

    const renderNewFlyoutMiniCart = () => {
      return (
        <Drawer
          data-testid="deals-bundle-drawer-sister-store-flyout"
          anchor={'right'}
          open={miniCartFlyoutVisible}
          onClose={closeMiniCartFlyout}
          PaperProps={{ className: styles.customPaperDrawer }}
          drawerRef={miniCartModalRef}
        >
          <TrapFocus open={miniCartFlyoutVisible}>
            <div
              className={styles.miniCartFlyoutDialog}
              role="dialog"
              aria-modal="true"
              aria-labelledby={MINI_CART_TITLE_ID}
            >
              <MiniCartFlyoutComp
                handleClose={closeMiniCartFlyout}
                isModalOpen={showMiniCartFlyoutModal}
                image={productImage}
                successData={cartSuccessData}
                productDetails={productDetails}
                miniCartData={miniCartMap}
                closeStoreModal={closeStoreModal}
                cartUrl={submitToCart}
                productTitle={productName.productPart}
                pageName="pdp"
                orderDetailsFromState={orderDetailsFromState}
                parentRef={miniCartModalRef}
                pageLevelCertonaRecommendations={certonaRecommendations}
              />
            </div>
          </TrapFocus>
        </Drawer>
      );
    };

    if (checkToOpenMiniCartModal) {
      if (isMiniCartFlyoutEnabled) {
        return renderNewFlyoutMiniCart();
      } else if (!isMobile) {
        return renderOldMiniCart();
      }
    }
  };

  const handleFilter = useLatestCallback((filter: HandleFilterType) => {
    const { checked, displayName, quickFilter, refinement, selectionType } = filter;
    const { label, properties } = refinement;
    const isQuickFilter = !!quickFilter && !isDesktop;
    const filterName = `${selectionType}: ${displayName}: ${label}`;
    const filterId = properties.filters;

    dispatch(setFilterScrollBrand(true));

    dispatchSetProductFilterEvents(filterName, filterId, Boolean(checked), isQuickFilter);
  });
  const { renderDealDrawer, handleDealClick } = useDealDrawer();

  const handleBackButton = useLatestCallback(() => {
    sessionStorage.setItem('returnToSearchResults', 'true');
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['productSkuDetails'] });
    };
    // This should only be run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const shelfPageBodyCMSContent = plpCMSData?.shelfpagebody;
  const shelfHeaderCMSContent = plpCMSData?.shelfresultheader;

  return (
    <div ref={mainShelfRef}>
      {isMobileFilterOpen && (
        <FilterMobileDrawer
          staticFilterData={staticFilterData}
          dynamicFilterData={shelfLeftNav?.[0]?.dynamicNavigation?.attributeNarrowingList}
          pageType="ProductShelf"
          showSpinner={shelfRequestFetching}
          onFilterSelect={handleFilter}
        />
      )}
      <div
        className={cx(
          azCommonStyles.mainContentContainer,
          styles.pageContent,
          styles.shelfContent,
          { [styles.placeholderHeight]: shelfRequestFetching },
        )}
      >
        {isMiniCartFlyoutEnabled ? (
          <div data-testid="mini-cart-flyout">{shelfRequestSuccess && renderMiniCart()}</div>
        ) : (
          <div data-testid="mini-cart-flyout">
            {shelfRequestSuccess && isDesktop && renderMiniCart()}
          </div>
        )}
        {shelfHeaderCMSContent?.length ? (
          <CMSShelfResultHeaderModularHandler
            catLabel={shelfCatLabel}
            content={shelfHeaderCMSContent}
            seoTopCopy={seoTopCopy}
            shelfBreadcrumb={shelfBreadcrumb}
            showSEOData={!makeModelYearPageType}
            shortDescription={shortDescription}
            handleClick={handleBackButton}
          />
        ) : null}

        {renderDealDrawer()}

        {isMerchShelfPage && plpCMSData?.additional_attributes_merch_shelf_page?.seo ? (
          <CMSSeoTags seoData={plpCMSData.additional_attributes_merch_shelf_page.seo} />
        ) : null}
        {isMerchShelfPage && plpCMSData?.additional_attributes_merch_shelf_page?.index === false ? (
          <Head>
            <meta key="metaRobots" name="robots" content="noindex, nofollow" />
          </Head>
        ) : null}
        {seoMetadataInfo ? (
          <ShelfSeoMetaData
            seoMetaData={seoMetadataInfo}
            parts={parts}
            shelfCatLabel={shelfCatLabel}
          />
        ) : null}
        <div ref={shelfRef}>
          {(showSkeleton ||
            isPLPCMSDataFetching ||
            isProductResultsFetching ||
            productsResponseFailed) &&
          !loadMoreClicked ? (
            <ShelfSkeleton />
          ) : (
            <View
              className={cx(styles.shelfNavAndResultsContainer, {
                [styles.noFilters]: !hasFilters,
              })}
            >
              {hasFilters && shelfLeftNav && (isDesktop || isABot) ? (
                <View className={styles.nav}>
                  <CMSShelfLeftNav content={shelfLeftNav} onFilterSelect={handleFilter} />
                </View>
              ) : null}
              <View className={styles.results} maxWidth={{ m: '100%' }}>
                {shelfPageBodyCMSContent?.length ? (
                  <CMSPLPBodyModularHandler
                    pageType="shelf"
                    content={shelfPageBodyCMSContent}
                    refQuickFilter={refQuickFilter}
                    resultsListRef={resultsListRef}
                    onFilterSelect={handleFilter}
                    handleDealClick={handleDealClick}
                    makeModelYearPageType={!!shelfData?.makeModelYearPageType}
                  />
                ) : null}
              </View>
            </View>
          )}
        </div>
      </div>

      {!shelfRequestFetching && !isFetchingLocation && (
        <>
          {plpCMSData?.shelfresultbottom?.length ? (
            <CMSResultBottom
              content={plpCMSData.shelfresultbottom}
              onCertonaAddToCartClick={() => {
                setCertonaRecommendationAddedToCart(true);
              }}
              setAddedCertonaItemObject={setAddedCertonaItemObject}
              certonaRecommendations={certonaRecommendations}
              faqsData={seoFaqsData}
            />
          ) : null}
          <BazaarVoiceShelf
            productId={shelfProductId}
            makeModelYearPageType={makeModelYearPageType}
          />
          {!isBrandShelfPage(legacyRouter.asPath) && !isMerchShelfPage && <PreFooter />}
        </>
      )}
      <div className={styles.miniCartPopup}>
        <Drawer
          data-testid="mini-cart-flyout"
          anchor={'right'}
          open={showCertonaMiniCartFlyoutModal}
          onClose={toggleCertonaMiniCartFlyout}
          PaperProps={{ className: styles.customPaperDrawer }}
          drawerRef={lazyMiniCartModalRef}
        >
          <TrapFocus open={showCertonaMiniCartFlyoutModal}>
            <div className={styles.miniCartFlyoutDialog} role="dialog" aria-modal="true">
              <Suspense>
                {showCertonaMiniCartFlyoutModal && addedCertonaItemObject && (
                  <LazyMiniCartFlyoutComp
                    handleClose={toggleCertonaMiniCartFlyout}
                    isModalOpen={showCertonaMiniCartFlyoutModal}
                    image={addedCertonaItemObject.productImage}
                    successData={addedCertonaItemObject.cartSuccessData}
                    productDetails={{
                      productName: addedCertonaItemObject.productDetails.productName,
                      certonaItemAddedTocart:
                        certonaRecommendationAddedToCart && isShelfCertonaAddToCartEnabled,
                      productPartType: addedCertonaItemObject.productDetails.partType,
                      productId: addedCertonaItemObject.productDetails.productId,
                      originalPartTypeId: addedCertonaItemObject.productDetails.partType,
                      productAvailabilityInfo: {
                        aZBadgesFlagVO: null,
                        dealsInfo: undefined,
                        skuPricingAndAvailability: undefined,
                      },
                    }}
                    miniCartData={miniCartMap}
                    closeStoreModal={closeStoreModal}
                    cartUrl={addedCertonaItemObject.submitToCart}
                    productTitle={addedCertonaItemObject.productTitle}
                    pageName={addedCertonaItemObject.pageName}
                    orderDetailsFromState={orderDetailsFromState}
                    skuId={addedCertonaItemObject.productDetails.skuId}
                    parentRef={lazyMiniCartModalRef}
                    pageLevelCertonaRecommendations={certonaRecommendations}
                  />
                )}
              </Suspense>
            </div>
          </TrapFocus>
        </Drawer>
      </div>
    </div>
  );
};
